import clsx from 'clsx';
import Link from 'next/link';
import React, { useCallback, useMemo } from 'react';
import { usePDPData } from '~/features/product-details/context';
import StarIconHalfActive from '~/icons/half-star-active.svg';
import StarIconActive from '~/icons/rating-active.svg';
import StarIconInactive from '~/icons/rating-inactive.svg';
import { Typography } from '~/shared/components';
import { useFrame } from '~/shared/utils';
import { useTranslation } from '~/shared/utils/translation';
import styles from './YotpoReviewsSummary.module.scss';

const MAX_STARS = 5;

// Set max decimal to 1 and remove unneeded 0s with `+`.
const formatRating = (rating: number) => +rating.toFixed(1);

type Props = React.HTMLAttributes<HTMLDivElement>;

export const YotpoReviewsSummary: React.FC<Props> = ({ className, ...rest }) => {
    const { translate } = useTranslation();
    const { data: frame } = useFrame();
    const { product, yotpoIdentifier } = usePDPData();

    // The star maths runs inside a hook, so it cannot sit behind the `!rating` guard below and
    // needs a value either way. Nothing renders at 0, so the default never reaches the screen.
    const { c_yotpoAverageRating: rating = 0, c_yotpoReviewCount: totalReviews } = product || {};

    const onLinkClick = useCallback(() => {
        if (!rating) return;

        // Hash navigation does not reliably move focus, so we do it ourselves.
        document.getElementById(yotpoIdentifier)?.focus();
    }, [rating, yotpoIdentifier]);

    const stars = useMemo(() => {
        const renderStar = (index: number) => {
            const starValue = index + 1;

            if (starValue <= rating) {
                return (
                    <StarIconActive className={styles.starSprite} data-fill="full" aria-hidden />
                );
            } else if (starValue - 0.5 <= rating) {
                return (
                    <StarIconHalfActive
                        className={styles.starSprite}
                        data-fill="half"
                        aria-hidden
                    />
                );
            } else {
                return <StarIconInactive className={styles.starSprite} aria-hidden />;
            }
        };

        const visual = [...Array(MAX_STARS)].map((_, index: number) => (
            <div className={styles.star} key={rating + index}>
                {renderStar(index)}
            </div>
        ));

        return visual;
    }, [rating]);

    if (!rating) return null;

    return (
        <div className={clsx(styles.yotpoInlineRating, className)} {...rest}>
            <Link
                className={styles.yotpoInlineStarsLink}
                href={`#${yotpoIdentifier}`}
                onClick={onLinkClick}
            >
                <div
                    className={styles.yotpoInlineStars}
                    role="img"
                    aria-label={translate('accessibility.ratingOutOfMax', {
                        rating: formatRating(rating).toString(),
                        max: MAX_STARS.toString(),
                    })}
                >
                    {stars}
                </div>
            </Link>

            <Link
                className={styles.yotpoInlineLabel}
                href={`#${yotpoIdentifier}`}
                onClick={onLinkClick}
            >
                <Typography variant="paragraph-S">
                    {translate('pdp.overview.ratingsSummary', {
                        rating: (totalReviews ?? 0).toLocaleString(frame.culture),
                    })}
                </Typography>
            </Link>
        </div>
    );
};
